import { ajvResolver } from "@hookform/resolvers/ajv";
import { ArrowBack } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Form } from "components/form";
import { useAlert } from "contexts/alert";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import QbemApiConfigSchema, { IQbemApiConfigForm } from "schema/automation/qbem-api-config";
import api from "services/api";
import {
  IEstipulanteItem,
  IListEstipulantesResponse,
  IListSubEstipulantesResponse,
  IQbemApiConfigResponse,
  ISubEstipulanteItem,
} from "./AppQbemApi.types";

interface IProps {
  workspaceId?: string;
  onSave?: () => void;
  isModal?: boolean;
}

// Mirrors MIN_ESTIPULANTE_SEARCH_LENGTH on the backend: shorter terms match
// nearly the whole roster (a single "A" matched 5496 of 5561).
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 450;
const DEFAULT_QBEM_BASE_URL = "https://eb.qbem.net.br";

const dataBr = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

function idContratoQbemApoio(data: IQbemApiConfigResponse | null): string {
  if (!data) return "";
  if (data.coletaPeloWorkspace === false)
    return "coleta pela organização (fora da lista): o id é preenchido pela coleta quando o contrato entrar na lista";
  switch (data.idContratoQbemStatus) {
    case "identificado":
      return `identificado (${data.idContratoQbem}), conferido em ${dataBr(data.idContratoQbemConferidoEm)}`;
    case "ausente_da_fatia":
      return `identificado, ausente da última fatia, conferido em ${dataBr(data.idContratoQbemConferidoEm)}`;
    case "apolice_nao_cadastrada":
      return "não identificado: sem apólice cadastrada";
    case "nao_encontrado_na_fatia":
      return "não identificado: apólice não encontrada na carteira";
    case "ambiguo":
      return "não identificado: apólice encontrada em mais de um contrato";
    // why: F5 da revisão final, spec 27/10 seção K. A coleta achou o contrato e não teve autor para gravar o id.
    case "sem_usuario_de_servico":
      return "não identificado: encontrado na carteira, sem usuário de serviço para gravar";
    default:
      return data.coletaPeloWorkspace ? "não identificado: sem coleta desde a ligação" : "";
  }
}

const estipulanteLabel = (option: IEstipulanteItem) =>
  option.cnpj ? `${option.nome} (${option.cnpj})` : option.nome;

export function AppQbemApiView({ workspaceId: propWorkspaceId, onSave, isModal }: IProps = {}) {
  const { workspaceId: paramWorkspaceId } = useParams<{ workspaceId: string }>();
  const workspaceId = propWorkspaceId ?? paramWorkspaceId;
  const history = useHistory();
  const { openAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingEstipulantes, setIsFetchingEstipulantes] = useState(false);
  const [estipulanteSearch, setEstipulanteSearch] = useState("");
  const [estipulantes, setEstipulantes] = useState<IEstipulanteItem[]>([]);
  const [searchHint, setSearchHint] = useState<string | null>(null);
  // Identifies the option currently bound to the form, so the field can display a
  // saved estipulante that no search has returned yet.
  const [selectedEstipulante, setSelectedEstipulante] = useState<IEstipulanteItem | null>(null);
  const [isFetchingSubEstipulantes, setIsFetchingSubEstipulantes] = useState(false);
  const [subEstipulantes, setSubEstipulantes] = useState<ISubEstipulanteItem[]>([]);
  const [configAtual, setConfigAtual] = useState<IQbemApiConfigResponse | null>(null);
  const searchGeneration = useRef(0);
  const subestipulanteFieldRef = useRef<HTMLDivElement | null>(null);
  const [subMenuWidth, setSubMenuWidth] = useState<number | undefined>(undefined);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    resetField,
    watch,
    control,
    formState: { errors, dirtyFields },
  } = useForm<IQbemApiConfigForm>({
    resolver: ajvResolver(QbemApiConfigSchema),
    // baseUrl defaults to the real QBem host rather than living only as a
    // placeholder: the estipulante search requires it, and an empty field made the
    // search silently unavailable while looking filled in.
    defaultValues: { email: "", senha: "", idEstipulante: "", subestipulante: "", idContratoQbem: "", baseUrl: DEFAULT_QBEM_BASE_URL, autoConfirmar: false, enableChangeMovements: false, enviarAoQbem: true },
  });

  const selectedIdEstipulante = watch("idEstipulante");
  const selectedSubestipulante = watch("subestipulante");
  const enviarAoQbem = watch("enviarAoQbem");

  // Deliberately NOT `watch("email")` etc. Watching the credential fields
  // re-rendered this component on every keystroke, which remounted the focused
  // input; the Modal's FocusTrap then saw activeElement fall to <body> and pulled
  // focus back to the first tabbable field (Email). Subscribing manually and
  // storing only the derived boolean re-renders when it flips, not per character.
  const [hasCredentials, setHasCredentials] = useState(false);
  useEffect(() => {
    const evaluate = (values: Partial<IQbemApiConfigForm>) =>
      setHasCredentials(
        Boolean(values.email?.trim() && values.senha?.trim() && values.baseUrl?.trim())
      );
    evaluate(getValues());
    const { unsubscribe } = watch((values) => evaluate(values));
    return unsubscribe;
  }, [watch, getValues]);

  useEffect(() => {
    if (!workspaceId) return;
    api
      .get<IQbemApiConfigResponse>(`/v2/workspaces/${workspaceId}/qbem-api-config`)
      .then((res) => {
        const data = res.data;
        setConfigAtual(data);
        // why: F2 da revisão final. O valor carregado vira o default do campo, para
        // dirtyFields dizer se o usuario mexeu (limpar um id carregado conta).
        resetField("idContratoQbem", {
          defaultValue: typeof data.idContratoQbem === "number" ? String(data.idContratoQbem) : "",
        });
        // Fora do isConfigured: um contrato pode estar desligado sem nunca ter
        // tido credencial.
        setValue("enviarAoQbem", data.enviarAoQbem !== false);
        if (data.isConfigured) {
          setValue("email", data.email ?? "");
          setValue("idEstipulante", data.idEstipulante ?? "");
          setValue("subestipulante", data.subestipulante ?? "");
          setValue("baseUrl", data.baseUrl || DEFAULT_QBEM_BASE_URL);
          setValue("autoConfirmar", !!data.autoConfirmar);
          setValue("enableChangeMovements", !!data.enableChangeMovements);
          // The saved config carries no estipulante name, so the field shows the id
          // until the user searches. Without this the Autocomplete would render
          // empty and a save could silently overwrite a config never displayed.
          if (data.idEstipulante) {
            const saved: IEstipulanteItem = {
              idEstipulante: Number(data.idEstipulante),
              nome: `ID ${data.idEstipulante} (configuração atual)`,
              cnpj: null,
              grupoEconomico: null,
            };
            setSelectedEstipulante(saved);
            setEstipulanteSearch(estipulanteLabel(saved));
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load QBem API config for prefill:", err);
        openAlert(
          err?.response?.data?.data?.message ??
            "Erro ao carregar configuração existente. O formulário continua disponível para preenchimento.",
          "error",
        );
      });
  }, [workspaceId]);

  // Searches by name rather than listing everything: the roster is ~5561 rows,
  // QBem caps pages at 100 and throttles after ~12 calls, so a full listing took
  // minutes and often failed outright. A name search answers in ~250ms, which is
  // fast enough to run as the user types.
  //
  // Debounced and generation-guarded: QBem starts returning 429 under bursts, and
  // without the guard a slow early response could land after a later one and
  // repopulate the list with results for a stale term.
  useEffect(() => {
    const termo = estipulanteSearch.trim();
    if (termo.length < MIN_SEARCH_LENGTH) {
      setEstipulantes([]);
      return;
    }
    // Picking an option puts its label in the input; that is not a new search.
    if (selectedEstipulante && termo === estipulanteLabel(selectedEstipulante)) return;
    // The field is disabled without credentials, so this only guards the case where
    // one is cleared after a term was already typed.
    if (!hasCredentials) return;

    const email = getValues("email");
    const senha = getValues("senha");
    const baseUrl = getValues("baseUrl");

    setSearchHint(null);
    const generation = ++searchGeneration.current;
    const timer = setTimeout(() => {
      setIsFetchingEstipulantes(true);
      api
        .post<IListEstipulantesResponse>("/v2/qbem-api/estipulantes", {
          email,
          senha,
          baseUrl: baseUrl || undefined,
          nome: termo,
        })
        .then((res) => {
          if (generation !== searchGeneration.current) return;
          const list = [...res.data.estipulantes].sort((a, b) =>
            a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
          );
          setEstipulantes(list);
          setSearchHint(list.length === 0 ? `Nenhum estipulante encontrado para "${termo}".` : null);
        })
        .catch((err) => {
          if (generation !== searchGeneration.current) return;
          setEstipulantes([]);
          // Shown inline instead of as a toast: while typing, a transient QBem
          // failure would otherwise stack alerts on every keystroke.
          setSearchHint(
            err?.response?.data?.data?.message ??
              "Erro ao buscar estipulantes. Verifique as credenciais."
          );
        })
        .finally(() => {
          if (generation === searchGeneration.current) setIsFetchingEstipulantes(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [estipulanteSearch]);

  // Fetch SubEstipulantes for a given Estipulante.
  // The CodigoEmpresa stored in `subestipulante` is the value sent to QBem's
  // /BeneficiarioApi/Alterar in the SubEstipulanteCNPJ field (alphanumeric per layout doc).
  //
  // This is called explicitly from the selection site rather than from an effect
  // keyed on selectedIdEstipulante. The effect approach silently no-oped when the
  // selected id didn't *change* — e.g. when picking the estipulante already loaded
  // from saved config — so no request fired and the SubEstipulante list stayed empty.
  //
  // Note: credentials (email/senha/baseUrl) are read via getValues() at call time.
  // The expected UX is: type creds → search by name → pick one → SubEstipulantes
  // load. If the user edits credentials *after* picking an Estipulante they should
  // search and pick again.
  const fetchSubEstipulantes = (idEstipulante: string) => {
    const email = getValues("email");
    const senha = getValues("senha");
    const baseUrl = getValues("baseUrl");
    if (!email || !senha) return;

    setIsFetchingSubEstipulantes(true);
    api
      .post<IListSubEstipulantesResponse>("/v2/qbem-api/subestipulantes", {
        email,
        senha,
        baseUrl: baseUrl || undefined,
        idEstipulante,
      })
      .then((res) =>
        setSubEstipulantes(
          [...res.data.subestipulantes].sort((a, b) =>
            a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
          )
        )
      )
      .catch((err) =>
        openAlert(
          err?.response?.data?.data?.message ?? "Erro ao buscar subestipulantes.",
          "error"
        )
      )
      .finally(() => setIsFetchingSubEstipulantes(false));
  };

  // Select an Estipulante and (re)load its SubEstipulantes. Clears any previously
  // selected SubEstipulante so the form never retains a code from another Estipulante's list.
  const handleSelectEstipulante = (estipulante: IEstipulanteItem | null) => {
    setSelectedEstipulante(estipulante);
    setValue("idEstipulante", estipulante ? String(estipulante.idEstipulante) : "", {
      shouldValidate: true,
    });
    setValue("subestipulante", "", { shouldValidate: false });
    setSubEstipulantes([]);
    if (!estipulante) return;
    fetchSubEstipulantes(String(estipulante.idEstipulante));
  };

  const onSubmit = ({ idContratoQbem, ...resto }: IQbemApiConfigForm) => {
    setIsLoading(true);
    // why: F2 da revisão final, spec 27/10 D6. O id so vai quando o usuario editou o
    // campo; ausente, o backend nao mexe. Reenviar o id carregado numa troca de par
    // ressuscitaria o contrato do par velho.
    const contrato = dirtyFields.idContratoQbem
      ? { idContratoQbem: idContratoQbem.trim() ? Number(idContratoQbem) : null }
      : {};
    api
      // Desligar manda so o interruptor: a senha nunca vem preenchida, e mandar
      // o formulario faria o backend tentar regravar credenciais vazias.
      .put(`/v2/workspaces/${workspaceId}/qbem-api-config`, resto.enviarAoQbem
          ? { ...resto, ...contrato }
          : { enviarAoQbem: false })
      .then(() => {
        openAlert("Configurações salvas com sucesso.", "success");
        onSave?.();
      })
      .catch((err) =>
        openAlert(
          err?.response?.data?.data?.message ?? "Erro ao salvar configurações.",
          "error"
        )
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {!isModal && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <IconButton size="small" onClick={() => history.goBack()}>
            <ArrowBack color="primary" />
          </IconButton>
          <Typography variant="h6">Configurações QBem API</Typography>
        </div>
      )}

      <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
        <Form.Body>
          <Form.CheckBox
            name="enviarAoQbem"
            label={
              <>
                Enviar movimentações ao QBem pela API
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {enviarAoQbem
                    ? "Ao exportar, os arquivos sobem ao QBem antes do e-mail."
                    : "Desligado: ao exportar, nada vai ao QBem e o e-mail leva todos os arquivos. Credenciais e estipulante salvos antes ficam guardados para religar."}
                </Typography>
              </>
            }
            sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
            control={control}
            disabled={isLoading}
          />
          {enviarAoQbem && (
            <>
              <Form.Input
                name="email"
                label="Email"
                type="text"
                disabled={isLoading}
                error={!!errors.email}
                errorMessage={errors.email?.message}
                sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                register={register}
              />
              <Form.Input
                name="senha"
                label="Senha"
                type="password"
                disabled={isLoading}
                error={!!errors.senha}
                errorMessage={errors.senha?.message}
                sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                register={register}
              />
              <Form.Input
                name="baseUrl"
                label="URL Base"
                type="text"
                placeholder="https://eb.qbem.net.br"
                disabled={isLoading}
                error={!!errors.baseUrl}
                errorMessage={errors.baseUrl?.message}
                sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                register={register}
              />
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  // Required because this form also renders inside a Modal. By default
                  // the popper is portalled to document.body, i.e. OUTSIDE the Modal's
                  // FocusTrap; as soon as typing opens it, the trap sees focus leave
                  // its subtree and yanks it back to the first tabbable field (Email),
                  // making the field impossible to type in. Every other Autocomplete in
                  // this codebase sets this too.
                  disablePortal
                  // The options ARE the search results, so client-side filtering would
                  // hide valid matches (QBem matches substrings anywhere in the name,
                  // MUI's default filter does not).
                  filterOptions={(x) => x}
                  // Broad terms match hundreds of rows ("LTDA" matches 3309), so the
                  // listbox is capped and scrolls rather than filling the viewport.
                  ListboxProps={{ style: { maxHeight: 320 } }}
                  // The selected option must stay in `options` or MUI drops the value
                  // (and warns): a saved estipulante, or one whose search term has since
                  // been retyped, is not in the current result set.
                  options={
                    selectedEstipulante &&
                    !estipulantes.some((e) => e.idEstipulante === selectedEstipulante.idEstipulante)
                      ? [selectedEstipulante, ...estipulantes]
                      : estipulantes
                  }
                  value={selectedEstipulante}
                  inputValue={estipulanteSearch}
                  loading={isFetchingEstipulantes}
                  // NOT `disabled={... || !hasCredentials}`. Toggling `disabled` while
                  // the user types makes MUI recreate the input, so focus falls to
                  // <body> and the Modal's FocusTrap pulls it to the first tabbable
                  // field (Email). The credential requirement is enforced where it
                  // actually matters — the search effect won't fire without them.
                  disabled={isLoading}
                  onInputChange={(_, value) => setEstipulanteSearch(value)}
                  onChange={(_, value) => handleSelectEstipulante(value)}
                  getOptionLabel={estipulanteLabel}
                  isOptionEqualToValue={(option, value) => option.idEstipulante === value.idEstipulante}
                  noOptionsText={
                    estipulanteSearch.trim().length < MIN_SEARCH_LENGTH
                      ? `Digite ao menos ${MIN_SEARCH_LENGTH} caracteres`
                      : "Nenhum estipulante encontrado"
                  }
                  renderOption={(props, option) => (
                    // Unlike Select, Autocomplete's popper already matches the input
                    // width — the option only needs to truncate inside it.
                    <li
                      {...props}
                      key={option.idEstipulante}
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option.nome}
                      {option.cnpj ? ` (${option.cnpj})` : ""} — ID: {option.idEstipulante}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estipulante"
                      placeholder="Digite parte do nome"
                      error={!!errors.idEstipulante}
                      helperText={
                        errors.idEstipulante?.message ??
                        (!hasCredentials
                          ? "Preencha email, senha e URL base para buscar"
                          : searchHint ??
                            `Busque por parte do nome (mínimo ${MIN_SEARCH_LENGTH} caracteres)`)
                      }
                      inputProps={params.inputProps}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetchingEstipulantes ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              {selectedIdEstipulante && (
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.subestipulante} ref={subestipulanteFieldRef}>
                    <InputLabel>Subestipulante</InputLabel>
                    <Select
                      value={selectedSubestipulante}
                      label="Subestipulante"
                      onChange={(e) => setValue("subestipulante", String(e.target.value), { shouldValidate: true })}
                      disabled={isLoading || isFetchingSubEstipulantes}
                      // Measured on open: the menu is rendered in a portal, so it cannot
                      // inherit the field's width in CSS. Without a hard width the
                      // nowrap options below stretch the paper past the field and push
                      // the scrollbar out of view.
                      onOpen={() => setSubMenuWidth(subestipulanteFieldRef.current?.offsetWidth)}
                      MenuProps={{
                        PaperProps: { sx: { maxHeight: 320, width: subMenuWidth, maxWidth: subMenuWidth } },
                      }}
                    >
                      {subEstipulantes.map((sub) => (
                        <MenuItem
                          key={sub.idSubestipulante}
                          value={String(sub.codigoEmpresa ?? sub.idSubestipulante)}
                          // Company names run long; without truncation an option wraps
                          // to several lines. `display: block` is what lets ellipsis
                          // apply — MenuItem is a flex container by default, where
                          // text-overflow has no effect.
                          sx={{
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {sub.nome}
                          {sub.codigoEmpresa ? ` (${sub.codigoEmpresa})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    {isFetchingSubEstipulantes && (
                      <FormHelperText>Carregando subestipulantes...</FormHelperText>
                    )}
                    {/* Subestipulantes are fetched when an option is picked, so a
                        manually typed id leaves this list empty. Say so instead of
                        rendering a silently empty dropdown. */}
                    {!isFetchingSubEstipulantes && subEstipulantes.length === 0 && (
                      <FormHelperText>
                        Selecione o estipulante pela busca para carregar os subestipulantes.
                      </FormHelperText>
                    )}
                    {errors.subestipulante && (
                      <FormHelperText>{errors.subestipulante.message}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  {...register("idContratoQbem")}
                  fullWidth
                  size="small"
                  label="Contrato na QBem (id)"
                  inputProps={{ inputMode: "numeric" }}
                  disabled={isLoading}
                  error={!!errors.idContratoQbem}
                  helperText={errors.idContratoQbem?.message ?? idContratoQbemApoio(configAtual)}
                />
              </Grid>
              <Form.CheckBox
                name="autoConfirmar"
                label="Confirmar movimentações automaticamente na QBem (sem aprovação manual no portal)"
                sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                control={control}
                disabled={isLoading}
              />
              <Form.CheckBox
                name="enableChangeMovements"
                label={
                  <>
                    Enviar movimentações de alteração ao QBem
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      O QBem não exibe quais campos foram alterados, dificultando a ação do operador.
                      Recomendamos manter desativado e gerenciar alterações pelo Conducco. Ative apenas
                      para testes ou quando o QBem suportar visualização de diffs.
                    </Typography>
                  </>
                }
                sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                control={control}
                disabled={isLoading}
              />
            </>
          )}
        </Form.Body>
        <Form.Footer>
          <Form.Button
            label="SALVAR"
            type="submit"
            variant="contained"
            isLoading={isLoading}
          />
          <Form.Button
            label="CANCELAR"
            type="cancel"
            variant="text"
            isLoading={isLoading}
          />
        </Form.Footer>
      </Form.Root>
    </>
  );
}
